import React, { useEffect, useState } from 'react'
import t from '~t'
import { Button, Icon } from '~ui'
import { LTDHUNT_API } from '../config'
import { getLtdhuntSession, checkDuplicate, submitDeal, getDealById, resolveChatTarget } from '../client'
import styles from './cta.module.css'

const SYMBOLS = { BRL: 'R$', USD: '$', EUR: '€', GBP: '£' }

function priceText({ price, priceCurrency }) {
    if (price == null) return null
    const sym = SYMBOLS[priceCurrency]
    return sym ? `${sym} ${price}` : [price, priceCurrency].filter(Boolean).join(' ')
}

function dealLink(dup) {
    const tool = dup?.tool?.slug || dup?.tool?.name
    const dealSlug = dup?.deal?.slug
    return (dup?.type === 'deal' && tool && dealSlug)
        ? `${LTDHUNT_API}/tools/${encodeURIComponent(tool)}/deals/${encodeURIComponent(dealSlug)}`
        : `${LTDHUNT_API}/deals`
}

function dealPageLink(deal) {
    const tool = deal?.tool?.slug
    return (deal?.slug && tool)
        ? `${LTDHUNT_API}/tools/${encodeURIComponent(tool)}/deals/${encodeURIComponent(deal.slug)}`
        : `${LTDHUNT_API}/deals`
}

export default function DealCTA({ deal, onDiscuss }) {
    const url = deal?.url
    const [phase, setPhase] = useState('probing')
    const [dup, setDup] = useState(null)
    const [doneHref, setDoneHref] = useState(null)
    //undefined = resolving, object = ready, null = no thread for this page
    const [chatTarget, setChatTarget] = useState(undefined)

    useEffect(() => {
        if (!url) return
        let cancelled = false
        setPhase('probing')
        setChatTarget(undefined)

        ;(async () => {
            //Duplicate detection is public, so do it before the session gate.
            //Signed-out visitors should still be able to read discussions.
            const d = await checkDuplicate(url).catch(() => null)
            if (cancelled) return
            if (d?.exists) {
                setDup(d)
                setPhase('dup')
                //resolve the discussion target up front so the Discuss click
                //stays fully synchronous — Firefox's sidebarAction.open() is
                //gesture-gated and any await before it voids the gesture
                const target = d.tool?.slug
                    ? {
                        toolSlug: d.tool.slug, toolName: d.tool.name, toolLogo: d.tool.logo_url, toolId: d.tool.id,
                        //keep the deal identity on deal dups — features like
                        //reminders link to /deals/{slug}, not the tool page
                        ...(d.type === 'deal' && d.deal?.id
                            ? { type: 'deal', dealId: d.deal.id, dealSlug: d.deal.slug }
                            : { type: 'tool' }),
                    }
                    : await resolveChatTarget(url).catch(() => null)
                if (!cancelled) setChatTarget(target)
                return
            }

            const session = await getLtdhuntSession()
            if (cancelled) return
            if (session === null) return setPhase('signin')
            //false = probe failed; stay on 'ready' — a 401 on submit still
            //routes to sign-in, so nothing is lost by optimistically trying
            setPhase('ready')
        })()

        return () => { cancelled = true }
    }, [url])

    if (!url) return null

    const submit = async () => {
        setPhase('submitting')
        try {
            const res = await submitDeal({ url })
            //resolve the deal_id to a deep link — best effort, /deals stays
            //the fallback for pending or missing data
            const created = res?.data?.deal_id ? await getDealById(res.data.deal_id) : null
            if (created) setDoneHref(dealPageLink(created))
            setPhase(res?.pending ? 'pending' : 'done')
        } catch (e) {
            setPhase(/401/.test(String(e?.message)) ? 'signin' : 'error')
        }
    }

    const price = priceText(deal)
    const busy = phase === 'probing' || phase === 'submitting'

    return (
        <div className={styles.panel} data-variant={deal.isDeal ? 'deal' : 'page'}>
            <div className={styles.row}>
                {deal.image
                    ? <img className={styles.thumb} src={deal.image} alt='' />
                    : <Icon name='link' />}
                <div className={styles.meta}>
                    <div className={styles.title}>{deal.title || url}</div>
                    <div className={styles.sub}>
                        {[price, deal.store].filter(Boolean).join(' · ')}
                    </div>
                </div>
            </div>

            {phase === 'signin' && (
                //real anchors: Button as='a' gets role='button' + loses
                //primary styling on anchors — links navigate, so they
                //must be links
                <a className={styles.linkBtn} data-variant='primary'
                    href={`${LTDHUNT_API}/signin`} target='_blank' rel='noreferrer'>
                    {t.s('signInToLtdhunt')}
                </a>
            )}

            {phase === 'dup' && (
                <>
                    <a className={styles.linkBtn} href={dealLink(dup)} target='_blank' rel='noreferrer'>
                        <Icon name='check' />
                        {t.s('alreadyOnLtdhunt')}
                    </a>
                    {onDiscuss && (
                        //target is pre-resolved above — the click path must
                        //not await or Firefox drops the sidebar-open gesture
                        <Button variant='outline' disabled={chatTarget === undefined}
                            onClick={() => chatTarget
                                ? onDiscuss(chatTarget)
                                : window.open(dealLink(dup), '_blank') /*no thread — discuss on the site*/}>
                            <Icon name='micro_comment' />
                            {chatTarget === undefined ? '…' : t.s('discussOnLtdhunt')}
                        </Button>
                    )}
                </>
            )}

            {(phase === 'probing' || phase === 'ready' || phase === 'submitting' || phase === 'error') && (
                <>
                    {phase === 'error' && (
                        <div className={styles.error}>{t.s('dealSubmitFailed')}</div>
                    )}
                    <Button variant='primary' disabled={busy} onClick={submit}>
                        {phase === 'submitting'
                            ? '…'
                            : deal.isDeal ? t.s('saveDealToLtdhunt') : t.s('submitPageToLtdhunt')}
                    </Button>
                </>
            )}

            {(phase === 'done' || phase === 'pending') && (
                <a className={styles.linkBtn} href={doneHref || `${LTDHUNT_API}/deals`} target='_blank' rel='noreferrer'>
                    <Icon name='check' />
                    {phase === 'pending' ? t.s('dealProcessing') : t.s('dealSubmitted')}
                </a>
            )}
        </div>
    )
}
